import {
	DuplicateFile,
	ProcessingFile,
	UploadStatusStep,
} from '@dealstack/frontend/shared/components/Documents/types';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { hasFileAssociationWork } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/hooks/useFindRelatedFiles.utils';
import { DocumentLinkTarget } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import { useCallback } from 'react';

export const useFindRelatedFiles = ({
	dealId,
	target,
	setUploadStatusStep,
	localDuplicateFiles,
	setProcessingFiles,
	setRelatedFilesInDeal,
	uploadFiles,
}: {
	dealId: string;
	target?: DocumentLinkTarget;
	setUploadStatusStep: (uploadStatusStep: UploadStatusStep) => void;
	localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	};
	setProcessingFiles: (processingFiles: ProcessingFile[]) => void;
	setRelatedFilesInDeal: (relatedFilesInDeal: FindRelatedFilesRes[]) => void;
	uploadFiles: (props: {
		relatedFiles: FindRelatedFilesRes[];
		processingFiles: ProcessingFile[];
	}) => Promise<void>;
}) => {
	const filterOutDuplicateFiles = useCallback(
		(duplicateFilesToProcess?: DuplicateFile[]) => {
			const filesToProcess = duplicateFilesToProcess
				? duplicateFilesToProcess
				: localDuplicateFiles.filesWithDuplicates;

			const fileToProcess: ProcessingFile[] = [];
			filesToProcess.forEach((file) => {
				// only keep the selected file
				file.subRows?.forEach((f) => {
					if (f.isSelected) {
						fileToProcess.push({
							file: f.file,
							index: f.index,
							md5: f.md5,
							classifierName: file.classifierName,
							dealClassifierId: file.dealClassifierId,
						});
					}
				});
			});

			setProcessingFiles(fileToProcess);
			return fileToProcess;
		},
		[localDuplicateFiles.filesWithDuplicates, setProcessingFiles]
	);

	const findRelatedFiles = async (filesWithDuplicates: DuplicateFile[]) => {
		const processingFiles = filterOutDuplicateFiles(filesWithDuplicates);
		const relatedFiles = await DocumentService.findRelatedFiles({
			dealId,
			bodyPayload: {
				...target,
				fileData: processingFiles.map((file) => ({
					classifierName: file.classifierName,
					md5: file.md5,
					dealClassifierId: file.dealClassifierId,
					fileName: file.file.name,
				})),
			},
		});

		if (!relatedFiles || !(relatedFiles?.length > 0)) {
			throw new Error('Error with findRelatedDocuments');
		}

		setRelatedFilesInDeal(relatedFiles);

		// if there is nothing to be selected move on to the next step
		if (!hasFileAssociationWork(relatedFiles)) {
			setUploadStatusStep(UploadStatusStep.uploadComplete);
			await uploadFiles({ relatedFiles, processingFiles });
			return;
		}
	};

	return { findRelatedFiles };
};
